import React, { useState } from 'react';
import { Spinner, Icon } from 'ui';
import { File, MoreVertical, CheckCircle } from 'react-feather';
import { snakeCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import ClickAwayListener from 'components/shared/ClickAwayListener';
import { useParams } from 'react-router-dom';
import { routes as ROUTES } from 'constants/config';
import request from 'utils/request_ajax';
import DateFormat from 'constants/date_format';
import { Drag } from 'components/ui/Icon/Streamline';
import ActionMenu from './ActionMenu';
import * as actions from '../../../actions/journeyBuilder';
import ContentPicker from './ContentPicker';

const deleteStepRequest = (journeyId, stepId) => {
  return request.delete(ROUTES.JOURNEY.STEPS.RESOURCE(journeyId, stepId));
};

const Path = ({ step, listeners, attributes }) => {
  const [showActions, setShowActions] = useState(false);
  const { steps } = useSelector(state => state);
  const stepIdx = steps.findIndex(x => x.stepId === step.stepId);

  const dispatch = useDispatch();

  const { journeyId } = useParams();

  const handleDeleteStep = () => {
    deleteStepRequest(journeyId, step.stepId)
      .then(responseData => {
        dispatch(actions.removeStep({ index: stepIdx }));
        dispatch(
          actions.updateAutosavedAt({
            autosavedAt: moment().format(DateFormat.LONG_AT_TIME),
          }),
        );
      })
      .fail(err => {
        console.error(err);
      });
  };

  const handleEditStep = () => {
    dispatch(
      actions.updateStep({
        index: stepIdx,
        step: {
          ...step,
          editing: true,
        },
      }),
    );
  };

  return (
    <div className="JourneyBuilderContent JourneyPath">
      <div className="w-fill">
        <div className="JourneyBuilderContent-header">
          <h1 className="JourneyBuilderContent-title JourneyBuilderContent--path">
            <div {...listeners} {...attributes}>
              <Drag className="JourneyContent-icon--drag" size={16} />
            </div>
            <Icon className="JourneyContent-icon" name="path" />
            {step.title}
          </h1>
          <div className="JourneyBuilderContent-actions">
            {showActions && (
              <ClickAwayListener
                onClickAway={() => setShowActions(false)}
                touchEvent={false}
              >
                <ActionMenu
                  handleDelete={handleDeleteStep}
                  handleEdit={handleEditStep}
                />
              </ClickAwayListener>
            )}
            <MoreVertical
              className="JourneyContent-icon"
              size={20}
              onClick={() => setShowActions(true)}
            />
          </div>
        </div>
        <div className="JourneyPath-content">
          {step.contents ? (
            step.contents.map(content => {
              return (
                <div
                  key={snakeCase(content.type + content.id)}
                  className="JourneyPath-lesson"
                >
                  <File className="JourneyContent-icon" size={20} />
                  <span>{content.title}</span>
                </div>
              );
            })
          ) : (
            <>
              <Spinner />
              <span className="ml-8 c-brand-gray-darker">Loading...</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Lesson = ({ step, listeners, attributes }) => {
  const [showActions, setShowActions] = useState(false);
  const { steps } = useSelector(state => state);
  const stepIdx = steps.findIndex(x => x.stepId === step.stepId);

  const dispatch = useDispatch();
  const { journeyId } = useParams();

  const handleDeleteStep = () => {
    deleteStepRequest(journeyId, step.stepId)
      .then(() => {
        dispatch(actions.removeStep({ index: stepIdx }));
        dispatch(
          actions.updateAutosavedAt({
            autosavedAt: moment().format(DateFormat.LONG_AT_TIME),
          }),
        );
      })
      .fail(err => {
        //eslint-disable-next-line no-console
        console.error(err);
      });
  };

  const handleEditStep = () => {
    dispatch(
      actions.updateStep({
        index: stepIdx,
        step: {
          ...step,
          editing: true,
        },
      }),
    );
  };

  return (
    <div className="JourneyBuilderContent">
      <div className="w-fill">
        <div className="JourneyBuilderContent-header">
          <h1 className="JourneyBuilderContent-title">
            <div {...listeners} {...attributes}>
              <Drag className="JourneyContent-icon--drag" size={16} />
            </div>
            <File className="JourneyBuilderContent-icon" size={20} />
            {step.title}
          </h1>
          <div className="JourneyBuilderContent-actions">
            {showActions && (
              <ClickAwayListener
                onClickAway={() => setShowActions(false)}
                touchEvent={false}
              >
                <ActionMenu
                  handleDelete={handleDeleteStep}
                  handleEdit={handleEditStep}
                />
              </ClickAwayListener>
            )}
            <MoreVertical
              className="JourneyContent-icon"
              size={20}
              onClick={() => setShowActions(true)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Task = ({ step, listeners, attributes }) => {
  const [showActions, setShowActions] = useState(false);
  const { steps } = useSelector(state => state);
  const stepIdx = steps.findIndex(x => x.stepId === step.stepId);

  const dispatch = useDispatch();
  const { journeyId } = useParams();

  const handleDeleteStep = () => {
    deleteStepRequest(journeyId, step.stepId)
      .then(() => {
        request.delete(ROUTES.JOURNEY.TASK.RESOURCE(journeyId, step.contentId));
        dispatch(actions.removeStep({ index: stepIdx }));
        dispatch(
          actions.updateAutosavedAt({
            autosavedAt: moment().format(DateFormat.LONG_AT_TIME),
          }),
        );
      })
      .fail(err => {
        console.error(err);
      });
  };

  const handleEditStep = () => {
    dispatch(
      actions.updateStep({
        index: stepIdx,
        step: {
          ...step,
          editing: true,
        },
      }),
    );
  };

  return (
    <div className="JourneyBuilderContent">
      <div className="w-fill">
        <div className="JourneyBuilderContent-header">
          <h1 className="JourneyBuilderContent-title">
            <div {...listeners} {...attributes}>
              <Drag className="JourneyContent-icon--drag" size={16} />
            </div>
            <CheckCircle className="JourneyContent-icon" size={20} />
            {step.title}
          </h1>
          <div className="JourneyBuilderContent-actions">
            {showActions && (
              <ClickAwayListener
                onClickAway={() => setShowActions(false)}
                touchEvent={false}
              >
                <ActionMenu
                  handleDelete={handleDeleteStep}
                  handleEdit={handleEditStep}
                />
              </ClickAwayListener>
            )}
            <MoreVertical
              className="JourneyContent-icon"
              size={20}
              onClick={() => setShowActions(true)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ContentRenderer = ({
  type,
  step,
  idx,
  spacerHeight,
  listeners,
  attributes,
}) => {
  switch (type) {
    case 'Lesson':
      return step.contentId && !step.editing ? (
        <Lesson step={step} listeners={listeners} attributes={attributes} />
      ) : (
        <ContentPicker
          step={step}
          idx={idx}
          listeners={listeners}
          attributes={attributes}
        />
      );
    case 'Path':
      return step.contentId && !step.editing ? (
        <Path step={step} listeners={listeners} attributes={attributes} />
      ) : (
        <ContentPicker
          step={step}
          idx={idx}
          listeners={listeners}
          attributes={attributes}
        />
      );
    case 'Task':
      return step.contentId && !step.editing ? (
        <Task step={step} listeners={listeners} attributes={attributes} />
      ) : (
        <ContentPicker
          step={step}
          idx={idx}
          listeners={listeners}
          attributes={attributes}
        />
      );
    case 'Spacer':
      return (
        <div
          className="JourneyBuilderContent-spacer"
          style={{ height: spacerHeight }}
        />
      );
    default:
      return null;
  }
};

export default ContentRenderer;
